import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { StatusBadge, Money } from "@/components/shared/Common";
import { CalendarDays, Wallet, ShieldCheck, ArrowRight } from "lucide-react";

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [earn, setEarn] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/appointments/mine").then(r => setAppts(r.data));
    api.get("/doctor/earnings").then(r => setEarn(r.data));
    api.get("/doctor/me").then(r => setMe(r.data));
  }, []);

  const today = new Date().toISOString().slice(0,10);
  const todays = appts.filter(a => (a.slot_datetime||"").startsWith(today));
  const next = appts.find(a => a.status === "confirmed");
  const pendingNotes = appts.filter(a => a.status === "confirmed" && new Date(a.slot_datetime) < new Date()).length;

  return (
    <div className="space-y-6">
      <div>
        <div className="cm-eyebrow mb-2">Doctor dashboard</div>
        <h1 className="cm-h2">Good day, Dr. {user?.last_name}.</h1>
      </div>

      {me?.verification_status !== "verified" && (
        <div className="cm-card border-[#d97706]/30 bg-[#fffbeb]" data-testid="dr-verify-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-[#d97706]"/>
            <div>
              <div className="font-semibold text-[#92400e]">Verification: <StatusBadge status={me?.verification_status || "pending"}/></div>
              <div className="text-sm text-[#92400e]/80 mt-1">Your profile isn't visible to patients until your credentials are verified.</div>
              <Link to="/doctor/verification" className="text-sm font-semibold text-[#4a90d9] mt-2 inline-block">View verification →</Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Today's appointments" value={todays.length} testid="dr-stat-today"/>
        <Stat label="Pending notes" value={pendingNotes} testid="dr-stat-pending-notes"/>
        <Stat label="Paid earnings" value={<Money amount={earn?.total_paid || 0}/>} testid="dr-stat-paid"/>
        <Stat label="Pending payout" value={<Money amount={earn?.pending_amount || 0}/>} testid="dr-stat-pending"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 cm-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1a2b4a]">Today's schedule</h3>
            <Link to="/doctor/appointments" className="text-sm font-semibold text-[#4a90d9]" data-testid="dr-view-all-appts">All appointments <ArrowRight size={12} className="inline"/></Link>
          </div>
          {todays.length === 0 && <div className="text-sm text-[#475569]">Nothing on today. Time for a coffee.</div>}
          <div className="space-y-2">
            {todays.map(a => (
              <div key={a.id} className="p-3 rounded-lg border border-[#e2e8f0] flex items-center justify-between" data-testid={`dr-today-${a.id}`}>
                <div>
                  <div className="text-sm font-semibold text-[#1a2b4a]">{a.slot_datetime.slice(11,16)} · {a.consultation_type}</div>
                  <div className="text-xs text-[#475569]">{a.reason || "General consultation"}</div>
                </div>
                <Link to={`/doctor/consultation/${a.id}`} className="cm-btn-primary" data-testid={`dr-open-${a.id}`}>Open</Link>
              </div>
            ))}
          </div>
        </div>
        <div className="cm-card">
          <div className="cm-eyebrow mb-2"><Wallet size={12} className="inline mr-1"/>Earnings snapshot</div>
          <div className="text-3xl font-bold text-[#1a2b4a]"><Money amount={earn?.total_paid || 0}/></div>
          <div className="text-sm text-[#475569] mt-1">{earn?.paid_count || 0} consultations paid</div>
          <Link to="/doctor/earnings" className="cm-btn-outline w-full mt-4" data-testid="dr-earnings-link">Open earnings</Link>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="cm-card" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-[#4a90d9] font-semibold">{label}</div>
    <div className="text-2xl font-bold text-[#1a2b4a] mt-1">{value}</div>
  </div>
);
